// Section PRSNL Accompagnement - Compact avec accordéons
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, ChevronUp, Rocket, Calendar, Brain, TrendingUp,
  Zap, Target, CheckCircle, X, Award, Star
} from 'lucide-react';

// Accordéon compact
const Accordion = ({ title, icon: Icon, children, defaultOpen = false, color = '#E8621A' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color }} />
          <span className="text-sm font-bold text-gray-700">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-white text-sm">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Barre de progression des piliers
const PillarProgress = ({ icon, label, before, after }) => (
  <div className="flex items-center gap-2 py-1">
    <span className="text-base">{icon}</span>
    <div className="flex-1">
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="text-gray-400">{before}% → <span className="text-green-600 font-bold">{after}%</span></span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-orange-400 to-green-500 rounded-full transition-all duration-500" style={{ width: `${after}%` }} />
      </div>
    </div>
  </div>
);

// Section PRSNL Accompagnement
const PRSNLAccompagnementSection = ({ lead }) => {
  // Calcul des gains potentiels basé sur les données du lead
  const diag = lead?.diag || {};
  const ca = parseInt(String(diag.ca || '50000').replace(/[^0-9]/g, '')) || 50000;
  
  const fuitesStoppees = Math.round(ca * 0.15); // ~15% du CA en fuites récupérables
  const devActivee = Math.round(ca * 0.12); // ~12% de croissance
  
  return (
    <div className="space-y-2">
      {/* Header compact */}
      <div className="p-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold opacity-80">🔶 PRSNL</p>
            <p className="text-lg font-black">ACCOMPAGNEMENT 90 JOURS</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] opacity-80">Objectif</p>
            <p className="text-xs font-bold">+Rentabilité / +CA / Autonomie</p>
          </div>
        </div>
      </div>
      
      {/* Accordéons */}
      <Accordion title="🚀 Lancement" icon={Rocket} defaultOpen={false}>
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-50">
            <span className="text-lg">📞</span>
            <div>
              <p className="font-bold text-gray-800">1 visio stratégique (2h)</p>
              <p className="text-xs text-gray-500">Avec Sabril - Audit complet</p>
            </div>
          </div>
          <p className="text-xs text-gray-600">→ Plan d'action personnalisé dès J1</p>
        </div>
      </Accordion>
      
      <Accordion title="📆 Suivi Opérationnel (90j)" icon={Calendar}>
        <ul className="space-y-1 text-gray-700">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>1 point tous les 15 jours</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>Plan d'action concret à chaque session</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>Mise à jour des résultats & KPI</span>
          </li>
        </ul>
      </Accordion>
      
      <Accordion title="🧠 Outil de Pilotage" icon={Brain}>
        <ul className="space-y-1 text-gray-700">
          <li className="flex items-center gap-2">
            <span className="text-blue-500">📊</span>
            <span>Dashboard personnalisé</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-500">✅</span>
            <span>Actions claires, simples, chiffrées</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="text-purple-500">📈</span>
            <span>Suivi performances temps réel</span>
          </li>
        </ul>
      </Accordion>
      
      <Accordion title="📈 Ce que tu obtiens" icon={TrendingUp} defaultOpen={true} color="#16A34A">
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="p-2 rounded-lg bg-green-50">
              <p className="text-lg font-black text-green-600">+2€</p>
              <p className="text-[9px] text-gray-500">Ticket moyen</p>
              <p className="text-[10px] font-bold text-green-700">+4k€/mois</p>
            </div>
            <div className="p-2 rounded-lg bg-blue-50">
              <p className="text-lg font-black text-blue-600">-5%</p>
              <p className="text-[9px] text-gray-500">Coûts</p>
              <p className="text-[10px] font-bold text-blue-700">+2-5k€/mois</p>
            </div>
            <div className="p-2 rounded-lg bg-purple-50">
              <p className="text-lg font-black text-purple-600">+15%</p>
              <p className="text-[9px] text-gray-500">Clients</p>
              <p className="text-[10px] font-bold text-purple-700">+3-10k€/mois</p>
            </div>
          </div>
          <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center">
            <p className="text-[10px] opacity-80">💰 OBJECTIF GLOBAL</p>
            <p className="text-xl font-black">+8 000 à +15 000€/mois</p>
          </div>
        </div>
      </Accordion>
      
      <Accordion title="🔥 Transformation" icon={Zap} color="#DC2626">
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 rounded-lg bg-red-50 border border-red-200">
            <p className="text-[10px] font-bold text-red-600 mb-1">❌ AVANT</p>
            <ul className="text-[11px] text-gray-600 space-y-0.5">
              <li>• Tu bosses trop</li>
              <li>• Tu subis ton resto</li>
              <li>• Marges instables</li>
            </ul>
          </div>
          <div className="p-2 rounded-lg bg-green-50 border border-green-200">
            <p className="text-[10px] font-bold text-green-600 mb-1">✅ APRÈS</p>
            <ul className="text-[11px] text-gray-600 space-y-0.5">
              <li>• Piloté par les chiffres</li>
              <li>• Système structuré</li>
              <li>• Business duplicable</li>
            </ul>
          </div>
        </div>
      </Accordion>
      
      <Accordion title="🏛️ Les 7 Piliers (Progression)" icon={Award} color="#7C3AED">
        <div className="space-y-1">
          <PillarProgress icon="🏛️" label="ADN & Fondations" before={25} after={95} />
          <PillarProgress icon="💰" label="Finances" before={35} after={90} />
          <PillarProgress icon="🍽️" label="Carte & Concept" before={40} after={92} />
          <PillarProgress icon="⚙️" label="Opérations" before={20} after={88} />
          <PillarProgress icon="🏆" label="Image de Marque" before={30} after={94} />
          <PillarProgress icon="📝" label="Réseaux & Contenu" before={25} after={90} />
          <PillarProgress icon="🤖" label="IA & Automatisation" before={10} after={85} />
        </div>
      </Accordion>
      
      {/* Résumé financier */}
      <div className="p-3 rounded-xl bg-gray-900 text-white">
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center">
            <p className="text-[10px] text-gray-400">💸 Fuites stoppées</p>
            <p className="text-sm font-black text-green-400">+{fuitesStoppees.toLocaleString('fr-FR')}€</p>
            <p className="text-[9px] text-gray-500">/mois</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400">📈 Dev. activé</p>
            <p className="text-sm font-black text-blue-400">+{devActivee.toLocaleString('fr-FR')}€</p>
            <p className="text-[9px] text-gray-500">/mois</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400">💳 Investissement</p>
            <p className="text-sm font-black text-orange-400">6 700€</p>
            <p className="text-[9px] text-gray-500">unique</p>
          </div>
        </div>
        <div className="p-2 rounded-lg bg-white/10 text-center">
          <p className="text-[10px] text-gray-300">
            La plupart des restaurateurs identifient <span className="text-green-400 font-bold">+5 000€</span> de pertes évitables en 30 jours.
          </p>
          <p className="text-xs font-bold text-orange-400 mt-1">
            L'accompagnement se finance lui-même. Le reste c'est du gain.
          </p>
        </div>
      </div>
      
      {/* Stats finales */}
      <div className="flex gap-2">
        <div className="flex-1 p-2 rounded-lg bg-orange-50 text-center">
          <p className="text-2xl font-black text-orange-600">1/3</p>
          <p className="text-[9px] text-gray-500">Restos ferment avant 3 ans</p>
        </div>
        <div className="flex-1 p-2 rounded-lg bg-green-50 text-center">
          <p className="text-2xl font-black text-green-600">+27%</p>
          <p className="text-[9px] text-gray-500">CA moyen après PRSNL</p>
        </div>
      </div>
    </div>
  );
};

export default PRSNLAccompagnementSection;
